import { SnapshotDay, SnapshotEntry, SnapshotImage, SnapshotNote, TravelPostSnapshot } from '../types';
import { parseTimeToMinutes, compareTimelineItems } from '../../timeUtils';

export interface BuildSnapshotOptions {
  itinerary: any;
  title?: string | null;
  city?: string | null;
  coverImage?: string | null;
  tags?: string[] | null;
  sourceItineraryId?: string | null;
  currentUser?: {
    id: string;
    display_name?: string;
    avatar_url?: string;
  } | null;
}

const SCHEMA_VERSION = '1';

const toArray = <T>(value: T | T[] | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const coerceText = (value: any, fallback = ''): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value.trim();
  return String(value);
};

const resolveImageUrl = (raw: any): string | undefined => {
  if (!raw) return undefined;
  return raw.url
    || raw.src
    || raw.image
    || raw.path
    || raw.image_url
    || raw.imageUrl
    || raw.photo
    || raw.uri;
};

const buildSnapshotImage = (raw: any): SnapshotImage | null => {
  if (!raw) return null;
  const normalized = typeof raw === 'string' ? { url: raw } : raw;
  const url = resolveImageUrl(normalized);
  if (!url) return null;
  
  // 只有原始数据是对象且明确包含 uploaded_by 字段时，才保留 uploaded_by
  // 字符串 URL（如 cover_image）不应该有 uploaded_by
  const hasUploadedBy = typeof raw === 'object' && raw !== null && 'uploaded_by' in raw;
  
  const result = {
    url,
    alt: coerceText(normalized.alt || normalized.caption || normalized.title || normalized.name || ''),
    width: typeof normalized.width === 'number' ? normalized.width : undefined,
    height: typeof normalized.height === 'number' ? normalized.height : undefined,
    key: normalized.key || normalized.id || undefined,
    uploaded_by: hasUploadedBy ? normalized.uploaded_by : undefined,
  };
  
  // 调试：打印带 uploaded_by 的图片
  if (result.uploaded_by) {
    console.log('[buildSnapshotImage] Image with uploaded_by:', {
      url: result.url.substring(0, 80),
      uploaded_by: result.uploaded_by,
      hasUploadedBy,
      rawType: typeof raw,
    });
  }
  
  return result;
};

const extractImages = (candidates: any[]): SnapshotImage[] => {
  const mapped: SnapshotImage[] = [];
  const seenUrls = new Set<string>();
  
  candidates.forEach((raw) => {
    const image = buildSnapshotImage(raw);
    if (image && !seenUrls.has(image.url)) {
      seenUrls.add(image.url);
      mapped.push(image);
    }
  });
  return mapped.slice(0, 9);
};

const collectNoteImages = (note: any): SnapshotImage[] => {
  const levelImages = toArray(note?.custom_payload?.images)
    .concat(toArray(note?.payload?.images))
    .concat(toArray(note?.local_images));
  return extractImages(levelImages);
};

const collectEntryGallery = (item: any): SnapshotImage[] => {
  // 收集卡片封面图片来源
  // 注意：不收集 image_gallery，因为那是用户上传的笔记图片，应该只在 notes 区域显示
  // 如果把 image_gallery 也加到这里，会导致图片重复显示（卡片封面显示一次，notes 再显示一次）
  const gallerySources = toArray(item?.gallery)
    .concat(toArray(item?.card?.cover_image))  // 卡片封面
    .concat(item?.card?.hero_image ? [item.card.hero_image] : [])
    .concat(toArray(item?.card?.gallery))  // 卡片自带的 gallery（非用户上传）
    // 不收集 image_gallery：.concat(toArray(item?.card?.image_gallery))
    .concat(toArray(item?.payload?.images))
    .concat(toArray(item?.payload?.imageUrl))  // 外部链接的封面图
    .concat(toArray(item?.card?.base_payload?.imageUrl))  // base_payload 中的封面图
    .concat(toArray(item?.images));
  return extractImages(gallerySources);
};

const buildSnapshotNote = (note: any, currentUser?: { id: string; display_name?: string; avatar_url?: string } | null): SnapshotNote => {
  const text = coerceText(note?.note || note?.payload?.note || note?.title || note?.payload?.title || '');
  
  // 优先使用 note 自带的 creator 信息，否则使用当前用户信息
  const hasExplicitCreator = note?.creatorId || note?.creator_id || note?.creator_profile;
  const author = hasExplicitCreator
    ? {
        id: note?.creatorId || note?.creator_id || null,
        name: note?.creator_profile?.display_name || note?.creator_name || null,
        avatarUrl: note?.creator_profile?.avatar_url || null,
      }
    : currentUser
    ? {
        id: currentUser.id,
        name: currentUser.display_name || null,
        avatarUrl: currentUser.avatar_url || null,
      }
    : undefined;
  
  return {
    id: coerceText(note?.id || note?.meta?.itineraryEntryId || note?.meta?.itinerary_entry_id || note?.created_at || Math.random()),
    text,
    images: collectNoteImages(note),
    author,
    createdAt: note?.created_at || note?.createdAt || null,
  };
};

const buildSnapshotEntry = (item: any, order: number, currentUser?: { id: string; display_name?: string; avatar_url?: string } | null): SnapshotEntry | null => {
  if (!item) return null;
  const rawTitle = item?.title || item?.payload?.title || item?.card?.title || item?.card?.name;
  const title = coerceText(rawTitle || `景點 ${order}`);
  const subtitleRaw = item?.payload?.subtitle || item?.card?.sub_title || item?.card?.subtitle || '';
  const subtitle = coerceText(subtitleRaw || '');
  const noteList = Array.isArray(item?.children) ? item.children : [];
  const notes = noteList.map(note => buildSnapshotNote(note, currentUser));
  const summary = coerceText(item?.note || notes[0]?.text || '');
  const gallery = collectEntryGallery(item);
  
  // 调试：打印卡片数据和收集到的图片
  console.log('[buildSnapshotEntry]', {
    title,
    cardId: item?.card?.id || item?.card_id,
    hasCard: !!item?.card,
    coverImage: item?.card?.cover_image,
    imageGallery: item?.card?.image_gallery,
    galleryLength: gallery.length,
    gallery: gallery.map(img => ({ url: img.url?.substring(0, 50), uploaded_by: img.uploaded_by })),
  });

  return {
    id: coerceText(item?.id || `entry-${order}`),
    sourceEntryId: coerceText(item?.id || item?.meta?.itineraryEntryId || item?.meta?.itinerary_entry_id || `entry-${order}`),
    order,
    title,
    subtitle: subtitle || undefined,
    summary: summary || undefined,
    meta: {
      itemType: item?.type,
      start_time: item?.time || item?.start_time || item?.meta?.start_time || item?.payload?.start_time || item?.custom_payload?.start_time,
      startTime: item?.startTime || item?.meta?.startTime,
      time: item?.time,
      end_time: item?.end_time || item?.meta?.end_time || item?.payload?.end_time || item?.custom_payload?.end_time,
      endTime: item?.endTime || item?.meta?.endTime,
      card_id: item?.card_id || item?.card?.id,
      card_slug: item?.card?.slug,
      mapLink: item?.payload?.mapLink || item?.custom_payload?.mapLink,
    },
    coverImage: gallery[0],
    gallery,
    notes,
    // 保存原始 payload 和 card 數據用於渲染
    payload: item?.payload || item?.custom_payload,
    card: item?.card,
  };
};

export const buildSnapshotFromItinerary = (options: BuildSnapshotOptions): TravelPostSnapshot => {
  const { itinerary } = options;
  const daysRaw = Array.isArray(itinerary?.days) ? itinerary.days : [];
  const snapshotDays: SnapshotDay[] = daysRaw.map((day: any, index: number) => {
    const entriesRaw = Array.isArray(day?.items) ? day.items : [];
    
    // 按時間排序條目
    const sortedItems = [...entriesRaw].sort((a, b) => compareTimelineItems(a, b, parseTimeToMinutes));
    
    const entries: SnapshotEntry[] = sortedItems
      .map((item: any, entryIndex: number) => buildSnapshotEntry(item, entryIndex + 1, options.currentUser))
      .filter((entry): entry is SnapshotEntry => Boolean(entry));

    return {
      id: `day-${day?.day || index + 1}`,
      dayNumber: typeof day?.day === 'number' ? day.day : index + 1,
      date: day?.date || undefined,
      title: day?.title || undefined,
      entries,
    };
  });

  const filteredDays = snapshotDays.filter((day) => day.entries.length > 0);
  const entryCount = filteredDays.reduce((acc, day) => acc + day.entries.length, 0);
  const mediaCount = filteredDays.reduce((acc, day) => acc + day.entries.reduce((sum, entry) => sum + entry.gallery.length + entry.notes.reduce((noteSum, note) => noteSum + note.images.length, 0), 0), 0);

  return {
    schemaVersion: SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    title: options.title || itinerary?.title || itinerary?.name,
    summary: itinerary?.summary || undefined,
    city: options.city || itinerary?.location || itinerary?.custom_city,
    coverImage: options.coverImage || filteredDays[0]?.entries[0]?.coverImage?.url,
    tags: options.tags || undefined,
    days: filteredDays,
    meta: {
      dayCount: filteredDays.length,
      entryCount,
      mediaCount,
      sourceItineraryId: options.sourceItineraryId || undefined,
    },
  };
};
